import React, { useMemo } from "react";

import cn from "classnames";
import styled, { keyframes } from "styled-components";

import colorSchema from "../../../assets/kits/colors/colors.module.scss";
import {
  checkFontClassExistence,
  checkPaddingMarginClassExistence,
} from "../../../utils/regexCheckers.ts";

const animatedLoading = keyframes`
  0% {
    opacity: 100%;
  }
  12.5% {
    opacity: 40%;
  }
  25% {
    opacity: 100%;
  }
  37.5% {
    opacity: 40%;
  }
  50% {
    opacity: 100%;
  }
  62.5% {
    opacity: 40%;
  }
  75% {
    opacity: 100%;
  }
  87.5% {
    opacity: 40%;
  }
  100% {
    opacity: 100%;
  }
`;

const ButtonStyles = styled.button`
  user-select: none;
  cursor: pointer;
  background-color: transparent;
  border: none;

  border-radius: 6px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  color: ${colorSchema.icon};

  &.disabled {
    cursor: default;
    user-select: none;
    // pointer-events: none;
    opacity: 0.3;
  }

  &.primary {
    background-color: ${colorSchema.icon};
    color: ${colorSchema.icon};
  }

  &.secondary {
    color: ${colorSchema.gray_1};
    border: 1px solid ${colorSchema.gray_2};
  }
  &.loading {
    ${({ buttonStyle }: StyledProps) =>
      buttonStyle !== "text" && `border: 1px solid ${colorSchema.green_1};`}
    fill: ${colorSchema.white};
    cursor: default;
    pointer-events: none;

    .loading-dot {
      animation: ${animatedLoading} 3s linear infinite;
      &:nth-child(1) {
        animation-delay: 0s;
      }
      &:nth-child(2) {
        animation-delay: -2s;
      }
      &:nth-child(3) {
        animation-delay: -1s;
      }
    }
  }
`;

type ButtonStyleType = "primary" | "secondary" | "text";

type ButtonStateType = "default" | "loading" | "disable";

type ButtonSizeType = "large" | "medium" | "small";

type iconPositionType = "right" | "center" | "left";

interface CustomButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  buttonStyle?: ButtonStyleType;
  state?: ButtonStateType;
  size?: ButtonSizeType;
  icon?: React.ReactNode;
  iconPosition?: iconPositionType;
  disabledOnClick?: React.MouseEventHandler<HTMLButtonElement>;
}

type StyledProps = {
  buttonStyle?: ButtonStyleType;
};

type directions = {
  t: string;
  b: string;
  l: string;
  r: string;
};

type PdCl<
  T = {
    large: directions;
    medium: directions;
    small: directions;
  }
> = {
  [key: string]: T;
};

const paddingClasses: PdCl = {
  rightIcon: {
    large: {
      t: "pd-t-12",
      b: "pd-b-12",
      l: "pd-l-12",
      r: "pd-r-16",
    },
    medium: {
      t: "pd-t-8",
      b: "pd-b-8",
      l: "pd-l-12",
      r: "pd-r-16",
    },
    small: {
      t: "pd-t-4",
      b: "pd-b-4",
      l: "pd-l-8",
      r: "pd-r-12",
    },
  },
  centerIcon: {
    large: {
      t: "pd-t-12",
      b: "pd-b-12",
      l: "pd-l-12",
      r: "pd-r-12",
    },
    medium: {
      t: "pd-t-8",
      b: "pd-b-8",
      l: "pd-l-8",
      r: "pd-r-8",
    },
    small: {
      t: "pd-t-6",
      b: "pd-b-6",
      l: "pd-l-6",
      r: "pd-r-6",
    },
  },
  leftIcon: {
    large: {
      t: "pd-t-12",
      b: "pd-b-12",
      l: "pd-l-16",
      r: "pd-r-12",
    },
    medium: {
      t: "pd-t-8",
      b: "pd-b-8",
      l: "pd-l-16",
      r: "pd-r-12",
    },
    small: {
      t: "pd-t-4",
      b: "pd-b-4",
      l: "pd-l-12",
      r: "pd-r-8",
    },
  },
  noIcon: {
    large: {
      t: "pd-t-12",
      b: "pd-b-12",
      l: "pd-l-16",
      r: "pd-r-16",
    },
    medium: {
      t: "pd-t-8",
      b: "pd-b-8",
      l: "pd-l-16",
      r: "pd-r-16",
    },
    small: {
      t: "pd-t-4",
      b: "pd-b-4",
      l: "pd-l-12",
      r: "pd-r-12",
    },
  },
  loading: {
    large: {
      t: "pd-t-20",
      b: "pd-b-20",
      l: "pd-l-16",
      r: "pd-r-16",
    },
    medium: {
      t: "pd-t-16",
      b: "pd-b-16",
      l: "pd-l-16",
      r: "pd-r-16",
    },
    small: {
      t: "pd-t-12",
      b: "pd-b-12",
      l: "pd-l-12",
      r: "pd-r-12",
    },
  },
};

const CustomButton = React.forwardRef<HTMLButtonElement, CustomButtonProps>(
  (
    {
      buttonStyle = "primary",
      state = "default",
      size = "medium",
      icon,
      iconPosition,
      className = "",
      children,
      disabledOnClick,
      onClick,
      disabled,
      ...props
    },
    ref
  ) => {
    const buttonProps: React.ButtonHTMLAttributes<HTMLButtonElement> = {
      ...props,
      // disabled: state === "disable" || props.disabled,
    };

    const hasIcon = useMemo(() => typeof icon !== "undefined", [icon]);
    const isIcon = useMemo(
      () => iconPosition === "center" && hasIcon,
      [hasIcon, iconPosition]
    );

    const padding = useMemo(() => {
      return paddingClasses[
        state === "loading"
          ? "loading"
          : hasIcon === false
          ? "noIcon"
          : `${iconPosition}Icon`
      ][size];
    }, [state, hasIcon, iconPosition, size]);

    const theClassName = useMemo(() => {
      if (padding === null) return className;
      let cn = className;
      if (!checkPaddingMarginClassExistence(cn, "padding", "t")) {
        cn += " " + padding.t;
      }
      if (!checkPaddingMarginClassExistence(cn, "padding", "b")) {
        cn += " " + padding.b;
      }
      if (!checkPaddingMarginClassExistence(cn, "padding", "l")) {
        cn += " " + padding.l;
      }
      if (!checkPaddingMarginClassExistence(cn, "padding", "r")) {
        cn += " " + padding.r;
      }
      return cn;
    }, [className, padding]);

    if (disabled === true) {
      state = "disable";
    }

    return (
      <ButtonStyles
        className={cn(
          {
            "txt-t1-m": !checkFontClassExistence(theClassName),
            loading: state === "loading",

            primary:
              !hasIcon && state !== "loading" && buttonStyle === "primary",
            secondary:
              !hasIcon && state !== "loading" && buttonStyle === "secondary",
            disabled: state === "disable" || disabled,
          },
          theClassName
        )}
        buttonStyle={buttonStyle}
        onClick={(event) => {
          if (state === "disable" || disabled) {
            disabledOnClick?.(event);
          } else if (state !== "loading") {
            onClick?.(event);
          }
        }}
        {...buttonProps}
        ref={ref}
      >
        {state === "loading" ? (
          <svg viewBox="0 0 36 8" height="8" width="36">
            <circle className="loading-dot" cx="4" cy="4" r="4" />
            <circle className="loading-dot" cx="18" cy="4" r="4" />
            <circle className="loading-dot" cx="32" cy="4" r="4" />
          </svg>
        ) : (
          <>
            {hasIcon && iconPosition === "right" && (
              <>
                {icon}
                <span className="pd-l-6" />
              </>
            )}
            {isIcon ? icon : children}
            {hasIcon && iconPosition === "left" && (
              <>
                <span className="pd-l-6" />
                {icon}
              </>
            )}
          </>
        )}
      </ButtonStyles>
    );
  }
);

export default CustomButton;

export type { CustomButtonProps };
